import { useState, useCallback, useEffect, useMemo, memo, useRef } from 'react';
import styled from '@emotion/styled';
import { Box, Typography, Skeleton } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import type { WidgetInstance, WidgetPropsByType } from '@trinity/chat';
import { SegmentationDashboardWidget } from '@trinity/chat';
import { primaryColorOrSentimentFallback } from '@trinity/chat-components/components/Theme';
import { WidgetMediaCard } from '@trinity/chat-components/components/Widget.Media/Widget.Media.Card';
import { useCurrentSegmentIdsContext } from '@trinity/chat-components/containers/Segmentation.Dashboard/context';
import { useWidgetGridWidgetsContext } from '@trinity/chat-components/containers/WidgetGrid/context';
import { ActivityIndicator } from '../presentation';
import { WidgetSourceInfo } from './WidgetSourceInfo';

type WidgetInstanceProps = WidgetPropsByType[SegmentationDashboardWidget.Media];

const MediaWidgetPreview = memo((props: WidgetInstanceProps) => {
  const [, setMediaRef] = useState<HTMLMediaElement | null>(null);
  return <WidgetMediaCard {...props} mediaSetting={props.media?.type ?? null} setMediaRef={setMediaRef} />;
});

MediaWidgetPreview.displayName = 'MediaWidgetPreview';

export interface WidgetSelectMediaStepProps {
  pickerOpen: boolean;
  onSelectedChange: (widget: WidgetInstance | null) => void;
  widgetType: 'media';
  /** Refreshes the host's widgets query on open; defaults to the legacy segment dashboard query key. */
  onOpenRefetch?: () => void;
}

export const WidgetSelectMediaStep = ({ pickerOpen, onSelectedChange, onOpenRefetch }: WidgetSelectMediaStepProps) => {
  const utils = useQueryClient();
  const [selected, setSelected] = useState<WidgetInstance | null>(null);
  const { widgets, widgetsLoading } = useWidgetGridWidgetsContext();
  const { projectId, segmentId } = useCurrentSegmentIdsContext();

  useEffect(() => {
    if (!pickerOpen) return;
    const id = setTimeout(() => setSelected(null), 0);
    if (onOpenRefetch) {
      onOpenRefetch();
    } else {
      utils.refetchQueries({
        queryKey: ['available-widgets', projectId, segmentId],
      }).catch(() => undefined);
    }
    return () => clearTimeout(id);
  }, [pickerOpen, projectId, segmentId, utils, onOpenRefetch]);

  useEffect(() => {
    onSelectedChange(selected);
  }, [selected, onSelectedChange]);

  const handleSelect = useCallback((w: WidgetInstance) => {
    setSelected(w);
  }, []);

  const previewComponentFor = useCallback((w: WidgetInstanceProps) => <MediaWidgetPreview {...w} />, []);

  const filteredWidgets = useMemo(
    () => widgets.filter((w) => w.type === SegmentationDashboardWidget.Media),
    [widgets]
  );

  if (widgetsLoading) {
    return <ActivityIndicator />;
  }

  return (
    <Box
      sx={{
        height: 'auto',
        overflowY: 'auto',
        pr: 1,
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': { width: '8px' },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': {
          background: 'var(--gray-l)',
          borderRadius: '4px',
        },
      }}
      role="list"
    >
      <Box sx={{ columnCount: { xs: 1, md: 1, lg: 1 }, columnGap: 2 }}>
        {filteredWidgets.map((w) => (
          <WidgetPreviewCard
            key={w.id}
            widget={w}
            isSelected={selected?.id === w.id}
            onSelect={handleSelect}
            renderPreview={previewComponentFor}
          />
        ))}
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Private shared card — mirrors WidgetSelectQuoteStep's WidgetPreviewCard
// ---------------------------------------------------------------------------

const WidgetPreviewCard = memo(function WidgetPreviewCard({
  widget,
  isSelected,
  onSelect,
  renderPreview,
}: {
  widget: WidgetInstance;
  isSelected: boolean;
  onSelect: (w: WidgetInstance) => void;
  renderPreview: (w: WidgetInstanceProps) => React.ReactNode;
}) {
  const [ref, inView] = useInViewport({ rootMargin: '600px' });
  const [ready, setReady] = useState(false);
  const preview = renderPreview(widget.props as WidgetInstanceProps);

  useEffect(() => {
    if (!inView || ready) return;
    const id = setTimeout(() => setReady(true), 10);
    return () => clearTimeout(id);
  }, [inView, ready]);

  return (
    <Box sx={{ breakInside: 'avoid', mb: 2 }}>
      <CardContainer onClick={() => onSelect(widget)} className={isSelected ? 'selected' : ''}>
        <Box
          ref={ref}
          flex={1}
          overflow="hidden"
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius={1}
        >
          {inView && ready ? (
            preview || (
              <Typography fontSize={12} color="var(--gray-d)">
                No preview
              </Typography>
            )
          ) : (
            <Skeleton variant="rounded" width="100%" height={180} />
          )}
        </Box>
        {widget.sourceMetadata && (
          <WidgetSourceInfo sourceMetadata={widget.sourceMetadata} projectSlug={null} />
        )}
      </CardContainer>
    </Box>
  );
});

const CardContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '18px',
  padding: '12px',
  gap: '6px',
  minHeight: '220px',
  cursor: 'pointer',
  transition: 'border-color 120ms ease, box-shadow 120ms ease',
  border: `2px solid ${theme.palette.gray.light2}`,
  contentVisibility: 'auto',
  containIntrinsicSize: '220px',
  willChange: 'transform',
  '&:hover, &.selected': {
    borderColor: primaryColorOrSentimentFallback(theme),
    backgroundColor: theme.palette.wl.enabled
      ? theme.palette.btnPrimary.bg
      : theme.palette.sentiment.primary.light,
  },
}));

function useInViewport({ rootMargin = '0px' }: { rootMargin?: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true);
            observer.disconnect();
            break;
          }
        }
      },
      { root: null, rootMargin, threshold: 0.01 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [rootMargin]);

  return [ref, inView] as const;
}
